/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, from, map, switchMap, take } from 'rxjs';
import { addDays, format, parse } from 'date-fns';
import { AddScheduleItem, SchedulesService } from '../services/schedules.service';
import { WorkforceService } from '../workforce-v2/workforce.service';
import { Schedule } from '../models/schedule.model';
import { User } from '../models/user.model';
import { AppSessionStore } from '../state/app-session.store';

/** Compatibility shape for the retained UI; employee planning is read/written in SQL. */
@Injectable()
export class SqlSchedulesService extends SchedulesService {
  private api = inject(WorkforceService);
  private session = inject(AppSessionStore);
  private refresh = new BehaviorSubject(0);
  readonly error = signal('');

  private async request(path: string, body: any) {
    this.error.set('');
    try {
 return await this.api.request(path, 'POST', body); 
} catch (error) {
 this.error.set('workforceCutover.failed'); this.refresh.next(this.refresh.value + 1); throw error; 
}
  }

  private day(value: string | Date) {
    return value instanceof Date ? format(value, 'yyyy-MM-dd') : /^\d{4}-/.test(value) ? value.slice(0, 10) : format(parse(value, 'd.M.yyyy', new Date()), 'yyyy-MM-dd');
  }

  override getList(options: any = {}) {
    return this.refresh.pipe(switchMap(()=>from(this.read(options))));
  }

  override getListOnce(options: any = {}) {
 return this.getList(options).pipe(take(1)); 
}

  override getSingle(options: any = {}) {
 return this.getList(options).pipe(map(rows=>rows[0] || null)); 
}

  override getSingleOnce(options: any = {}) {
 return this.getSingle(options).pipe(take(1)); 
}

  override getSchedules(site: string, _schedule: Schedule, start: Date, days: number) {
 return this.getList({site, dateBetween:{start, end:addDays(start, days * 3)}}); 
}

  override checkIfHas(user: string, site: string, start: Date, days: number) {
 return this.getList({user, dateBetween:{start, end:addDays(start, days - 1)}}).pipe(map(rows=>rows.some(r=>r.site !== site))); 
}

  private async read(options: any): Promise<Schedule[]> {
    const start = this.day(options.date || options.dateBetween?.start || addDays(new Date(), -31));
    const end = this.day(options.date || options.dateBetween?.end || addDays(new Date(), 31));
    const [rows, shifts, absences] = await Promise.all([
      this.api.request<any[]>(`allocation-workspace?start=${start}&end=${end}`),
      this.api.request<any[]>(`shifts?start=${start}&end=${end}`),
      this.api.request<any[]>(`absences?start=${start}&end=${end}`),
    ]);
    for (const shift of shifts) {
      if (!shift.cancelled && !rows.some(a => a.firestore_user_id === shift.firestore_user_id && a.firestore_site_id === shift.firestore_site_id && a.allocation_date === shift.shift_date)) {
        rows.push({...shift, sourceShift:true, allocation_date:shift.shift_date, version:shift.draft_revision, workspace_note:shift.note});
      }
    }
    if (!options.site) {
      for (const absence of absences.filter(a => !a.cancelled)) {
        rows.push({...absence, id:'absence/' + absence.id, firestore_site_id:'', allocation_date:start, start_time:null, end_time:null});
      }
    }
    const groups = new Map<string, any>();
    for (const row of rows){
      if (options.site && row.firestore_site_id !== options.site || options.user && row.firestore_user_id !== options.user) {
 continue; 
}
      const date = this.day(row.allocation_date), key = `${row.firestore_site_id}/${date}`;
      if (!groups.has(key)) {
 groups.set(key, {guid:key, contractor:this.session.contractorGuid(), site:row.firestore_site_id, date:format(new Date(date + 'T12:00:00'), 'd.M.yyyy'), dateObj:new Date(date + 'T12:00:00'), users:[], times:[], projects:[], information:[], machines:[], machinesGuids:[], locked:[], sqlRows:[]}); 
}
      const schedule = groups.get(key), user = row.firestore_user_id;
      schedule.sqlRows.push({...row});
      if (!schedule.users.includes(user)) {
 schedule.users.push(user); 
}
      schedule.times.push({user, start:row.start_time ? new Date(date + 'T' + row.start_time) : null, end:row.end_time ? new Date(date + 'T' + row.end_time) : null, status:row.status === 'draft' ? 'draft' : 'published'});
      schedule.projects.push({user, project:row.firestore_project_id || ''});
      schedule.information.push({user, text:row.workspace_note || row.title || ''});
      if (row.workspace_locked) {
 schedule.locked.push(user); 
}
      if (row.workspace_machine) {
 schedule.machines.push({user, machine:row.workspace_machine});schedule.machinesGuids.push(row.workspace_machine); 
}
    }
    return [...groups.values()];
  }

  override async save(schedule: Schedule) {
 return this.update(schedule); 
}

  override async update(schedule: Schedule): Promise<any> {
    const rows = (schedule as any).sqlRows || [];
    const users = [...new Set([...rows.map((r: any)=>r.firestore_user_id), ...schedule.users])];
    for (const user of users){
      const matching = rows.filter((r: any)=>r.firestore_user_id === user);
      const old = matching[0], time = schedule.times.find(t=>t.user === user);
      const clock = (value: any)=>value ? format(value.toDate?.() || new Date(value), 'HH:mm') : null;
      const body = {id:old?.id || null, version:old?.version ?? null, firestore_user_id:user, firestore_site_id:schedule.site, allocation_date:this.day(schedule.date), firestore_project_id:schedule.projects.find(p=>p.user === user)?.project || null, start_time:clock(time?.start), end_time:clock(time?.end), note:schedule.information?.find(i=>i.user === user)?.text || null, machine:schedule.machines?.find(m=>m.user === user)?.machine || null, locked:schedule.locked?.includes(user) || false, cancelled:!schedule.users.includes(user)};
      if (old && !body.cancelled && body.firestore_project_id === (old.firestore_project_id || null)
        && body.start_time === (old.start_time?.slice(0, 5) || null) && body.end_time === (old.end_time?.slice(0, 5) || null)
        && body.note === (old.workspace_note || old.title || null) && body.machine === (old.workspace_machine || null)
        && body.locked === !!old.workspace_locked) {
 continue; 
}
      if (matching.length > 1) {
 this.error.set('workforceV2.planning.multipleAllocations'); throw new Error('MULTIPLE_ALLOCATIONS'); 
}
      if (old?.sourceShift) {
        await this.request('shifts', {...body, id:old.id, version:old.draft_revision, start_date:body.allocation_date, end_date:body.allocation_date, weekdays:[0, 1, 2, 3, 4, 5, 6], unpaid_break_minutes:old.unpaid_break_minutes, allocation_id:old.allocation_id || null});
      } else {
        await this.request('allocation-workspace', body);
      }
    }
    this.refresh.next(this.refresh.value + 1);
    return schedule;
  }

  override async handleAddSchedule(item: AddScheduleItem, user: User, _schedule: Schedule) {
    const days: Date[] = [];
    if (item.deadlines?.length) {
 days.push(...item.deadlines); 
} else {
      let next = item.startDate;
      while (days.length < item.days){
        if (item.days === 1 || item.includeWeekend || ![0, 6].includes(next.getDay())) {
 days.push(next); 
}
        next = addDays(next, 1);
      }
    }
    for (const day of days){
      if (item.absence) {
        await this.request('absences', {firestore_user_id:user.guid, start_date:this.day(day), end_date:this.day(day), type:item.absence === 'holiday' ? 'HOLIDAY' : item.absence === 'sickLeave' ? 'SICKNESS' : 'OTHER', note:[item.absence, item.text].filter(Boolean).join(': ')});
        continue;
      }
      await this.request('allocation-workspace', {firestore_user_id:user.guid, firestore_site_id:item.site.guid, allocation_date:this.day(day), firestore_project_id:item.project || null, start_time:item.start ? format(item.start, 'HH:mm') : null, end_time:item.end ? format(item.end, 'HH:mm') : null, note:item.text || null, machine:item.machine || null, title:item.site.name});
    }
    if (item.roomTickets?.length) {
 this.addScheduleTicketsToUser(item.roomTickets, item.startDate, user); 
}
    this.refresh.next(this.refresh.value + 1);
  }
}
